/**
 * Snapshot interpolation buffer for REMOTE entities.
 *
 * Remote players are rendered `INTERPOLATION_DELAY_MS` (100ms) BEHIND the newest
 * snapshot, interpolating between the two snapshots that bracket that render
 * time. One dropped packet and a normal amount of jitter both disappear into
 * the delay instead of onto the screen.
 *
 * ── Why this replaces v1's extrapolation ──────────────────────────────────
 *
 * v1 (`RemotePlayers.js:192-213`) advanced every remote player by its last known
 * velocity every frame and then lerped toward the last known position. That
 * guesses the future, and it is wrong in exactly the moments that decide a
 * fight: an opponent who stops, turns, or dies keeps gliding for a frame or two
 * and then snaps back. With money on the line "I shot where he was drawn" is not
 * a bug report you can answer.
 *
 * Interpolating between two RECEIVED snapshots never guesses. The cost is a
 * fixed 100ms of latency on other players' positions, which is the correct
 * trade for an ability brawler with no hitscan and server-side hit resolution.
 */

import { INTERPOLATION_DELAY_MS } from '@chaos/protocol';

/** Older than this behind the render time and a snapshot can never be needed. */
const KEEP_MS = 1200;

export class SnapshotBuffer {
  constructor(delayMs = INTERPOLATION_DELAY_MS) {
    this.delayMs = delayMs;
    /** [{ recvAt, tick, players: Map<id, PlayerSnapshot> }], oldest first. */
    this.frames = [];
    /** Scratch reused every read so sampling allocates nothing per frame. */
    this._out = new Map();
  }

  push(snapshot, recvAt = performance.now()) {
    const players = new Map();
    for (const p of snapshot.players) players.set(p.id, p);
    this.frames.push({ recvAt, tick: snapshot.tick, players });

    const cutoff = recvAt - KEEP_MS;
    let drop = 0;
    while (drop < this.frames.length - 2 && this.frames[drop].recvAt < cutoff) drop++;
    if (drop > 0) this.frames.splice(0, drop);
  }

  /**
   * Samples every remote entity at `now - delayMs`.
   *
   * Returns a Map of id -> `{ x, y, z, yaw, health, phase, characterId, effects,
   * visible, extrapolated }`. `extrapolated` is true only when the buffer has run
   * dry, which is a stall, not a normal frame — the caller uses it to hold the
   * last pose rather than to invent motion.
   */
  sample(now = performance.now()) {
    this._out.clear();
    if (this.frames.length === 0) return this._out;

    const renderAt = now - this.delayMs;
    const newest = this.frames[this.frames.length - 1];

    // Buffer dry: the newest snapshot is already older than our render time.
    // Hold the last known pose. Extrapolating here is what v1 did, and it is
    // what produced the glide-and-snap.
    if (renderAt >= newest.recvAt) {
      for (const [id, p] of newest.players) {
        this._out.set(id, poseOf(p, p, 0, true));
      }
      return this._out;
    }

    // Default covers "renderAt predates everything we hold", which happens for
    // the first ~100ms after joining: draw the oldest pose, do not guess.
    let older = this.frames[0];
    let newer = this.frames[0];
    for (let i = this.frames.length - 1; i >= 0; i--) {
      if (this.frames[i].recvAt > renderAt) continue;
      older = this.frames[i];
      newer = this.frames[i + 1] ?? this.frames[i];
      break;
    }

    const span = newer.recvAt - older.recvAt;
    const t = span > 0 ? clamp01((renderAt - older.recvAt) / span) : 0;

    for (const [id, a] of older.players) {
      const b = newer.players.get(id) ?? a;
      this._out.set(id, poseOf(a, b, t, false));
    }
    // An entity that appears only in the newer frame (a decoy, a player who just
    // became visible) is drawn at its first known pose rather than held back a
    // further 100ms — popping in late reads worse than popping in on time.
    for (const [id, b] of newer.players) {
      if (!this._out.has(id)) this._out.set(id, poseOf(b, b, 0, false));
    }

    return this._out;
  }

  /** Wall-clock age of the newest snapshot. Feeds the debug readout. */
  ageMs(now = performance.now()) {
    const newest = this.frames[this.frames.length - 1];
    return newest ? now - newest.recvAt : 0;
  }

  clear() {
    this.frames.length = 0;
    this._out.clear();
  }
}

function poseOf(a, b, t, extrapolated) {
  return {
    id: a.id,
    characterId: a.characterId,
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
    z: a.z + (b.z - a.z) * t,
    yaw: a.yaw + shortAngle(a.yaw, b.yaw) * t,
    // Health and phase are discrete facts, not curves. Taking the NEWER value
    // means a health bar drops the instant the server says so even though the
    // body is still being drawn 100ms in the past.
    health: b.health,
    phase: b.phase,
    effects: b.effects,
    visible: b.visible,
    coins: b.coins ?? 0,
    // Retained for VFX that want a direction, never for position.
    vx: b.vx,
    vy: b.vy,
    vz: b.vz,
    extrapolated,
  };
}

function shortAngle(from, to) {
  let diff = to - from;
  while (diff > Math.PI) diff -= Math.PI * 2;
  while (diff < -Math.PI) diff += Math.PI * 2;
  return diff;
}

function clamp01(v) {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
